<template>
  <div class="volumes">
    <div class="volumes_header">
      <div class="volumes_title">
        <h1 class="subtitle">
          Volumes
        </h1>
        <div>
          <spinner
            v-if="loadingVolumes && !volumesError"
            width="20"
            height="20"
          />
          <svg-icon v-else-if="!loadingVolumes && volumesError" name="reload" class="svg_icon_position" size="medium" />
          <div v-else class="total_volumes">
            <span>{{ totalVolumes }}</span>
          </div>
        </div>
      </div>
      <div class="select_device">
        <el-select v-model="deviceType" :disabled="isDisabled">
          <el-option
            v-for="type in deviceOptions"
            :key="type.value"
            :label="type.label"
            :value="type.value"
          />
        </el-select>
      </div>
    </div>
    <div class="volumes_content">
      <template v-for="volume in volumes">
        <VolumesCard
          v-if="volume.show"
          :key="volume.label"
          :label="volume.label"
          :truncable="volume.truncable"
          :counter="volume.counter"
          :volume-key="volume.key"
          :icon="volume.icon"
          :show="volume.show"
          :loading="loadingVolumes"
          :error="volumesError"
          class="col_content"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { formatNumber } from '@/utils/helpers'
import VolumesCard from './VolumesCard.vue'

export default {
    name: 'Volumes',
    components: {
        VolumesCard
    },
    data() {
        return {
            deviceType: 'based',
            deviceOptions: [
                { value: 'based', label: 'ID Based' },
                { value: 'all', label: 'Cross Device' },
            ],
        }
    },
    mounted() {
        this.getFormulaMetrics({type: 'Volumes', formula: {v2: { recency: 30 }}})
    },
    watch: {
        xDeviceSetting: {
            immediate: true,
            deep: true,
            handler: function(newValue) {
                if(!newValue) {
                    this.deviceType = 'based'
                }
            }
        },
    },
    computed: {
        ...mapState('account', {
            accountId: state => state.accountId,
            accountLabel: state => state.label
        }),
        ...mapState('data/settings', {
            xDeviceSetting: state => state.general.xdevice,
        }),
        ...mapState('ui/settings', {
            firstPartyCookie: state => state.general.firstPartyCookie,
            providerFirstIdActivated: state => state.general.providerFirstIdActivated,
            providerUtiqActivated: state => state.general.providerUtiqActivated,
        }),
        ...mapState('data/volume', {
            volumes: state => state.volumes,
            loadingVolumes: state => state.loading,
            volumesError: state => state.error
        }),
        ...mapGetters({
            volumesCount: 'dashboardAccount/volumes',
            loadingVolumes: 'dashboardAccount/loadingVolumes',
            volumesError: 'dashboardAccount/volumesError',
        }),
        ...mapGetters('account', ['getIsMonitoringActionAllowed']),
        ...mapGetters({
            totalAudience: 'data/audience/getItems',
            audiencesActiveError: 'data/audience/audiencesActiveError',
            activeAudience: 'data/audience/getActiveItems',
            audienceIsReady: 'data/audience/isReadyForCriteriaList',
        }),
        ...mapGetters({
            transfersActiveError: 'data/transfer/transfersActiveError',
        }),
        isDisabled() {
            return this.loadingVolumes ||
              this.audiencesActiveError ||
              this.expiringAudiencesError ||
              this.transfersActiveError ||
              this.expiringTransfersError ||
              this.volumesError ||
              !this.xDeviceSetting ||
              (this.volumesCount && !this.volumesCount.total_xdevice)
        },
        activeAudienceCount() {
            return formatNumber(this.activeAudience().length)
        },
        activeTransfersList() {
            return this.transferList.filter(transfer => transfer.status === 'active').length
        },
        audienceLoading() {
            return !this.audienceIsReady()
        },

        audienceCount() {
            return formatNumber(this.totalAudience().length)
        },
        monitoringActionAllowed() {
            return this.getIsMonitoringActionAllowed()
        },
        totalVolumes() {
            const vc = this.volumesCount
            if (!vc || !vc.total_doc) return 0

            const DEVICE_LABEL = {
                all: 'Users',
                based: 'IDs'
            }

            const {
                deviceType,
                firstPartyCookie,
                providerFirstIdActivated,
                providerUtiqActivated
            } = this

            const subtractIf = (condition, value) => condition || !value ? 0 : value

            let total = deviceType === 'all'
                ? vc.total_xdevice.total_users
                : vc.total_doc

            if (deviceType === 'all') {
                total -= subtractIf(firstPartyCookie, vc.total_xdevice.total_users_desktop_first)
                total -= subtractIf(providerFirstIdActivated, vc.total_xdevice.total_users_firstid)
                total -= subtractIf(
                    providerUtiqActivated,
                    vc.total_xdevice.total_users_utiq_mobile + vc.total_xdevice.total_users_utiq_home
                )
            } else {
                total -= subtractIf(firstPartyCookie, vc.total_desktop_first)
                total -= subtractIf(providerFirstIdActivated, vc.total_firstid)
                total -= subtractIf(
                    providerUtiqActivated,
                    vc.total_utiq_mobile + vc.total_utiq_home
                )
            }
            return `${formatNumber(total)} ${DEVICE_LABEL[deviceType]}`
        },
        volumes() {
            return [{
                label: 'Web',
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_desktop', this.deviceType),
                truncable: false,
                key: 'total_users_desktop',
                icon: 'desktop-volume',
                show: this.isShowable('total_users_desktop', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: `Cookies (3rd Party - ${this.accountLabel})`,
                truncable: true,
                counter: !this.volumesCount ? null : formatNumber(this.volumesCount.dashboard.total_assigned),
                key: 'total_desktop',
                icon: 'cookie',
                show: this.isShowable('total_desktop_third_customer', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: 'Cookies (3rd Party - Weborama)',
                truncable: false,
                counter: !this.volumesCount ? null :  formatNumber(this.volumesCount.dashboard.total_not_assigned),
                key: 'total_desktop',
                icon: 'cookie',
                show: this.isShowable('total_desktop_third_weborama', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: this.deviceType === 'based'? 'Cookies (1st Party)' : 'Web (1st Party)',
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_desktop_first', this.deviceType),
                key: this.deviceType === 'based' ? 'total_desktop_first' : 'total_users_desktop_first',
                icon: this.deviceType === 'based' ? 'cookie' : 'desktop-first',
                show: this.isShowable('total_desktop_first', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: this.deviceType === 'based'? 'Mobile' : 'App',
                truncable: false,
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_mobile', this.deviceType),
                icon: 'mobile-volume',
                key: 'total_mobile',
                show: this.isShowable('total_mobile', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: 'First id',
                truncable: false,
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_firstid', this.deviceType),
                key: 'total_firstid',
                icon: 'first-id-volume',
                show: this.isShowable('total_firstid', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: 'Utiq Mobile',
                truncable: false,
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_utiq_mobile', this.deviceType),
                key: 'total_utiq_mobile',
                icon: 'utiq',
                show: this.isShowable('total_utiq_mobile', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: 'Utiq Home',
                truncable: false,
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_utiq_home', this.deviceType),
                key: 'total_utiq_home',
                icon: 'utiq',
                show: this.isShowable('total_utiq_home', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            {
                label: 'CRM IDs',
                truncable: false,
                counter: this.retrieveVolumes(this.loadingVolumes, 'total_crm', this.deviceType),
                icon: 'crm-id',
                key: 'total_crm',
                show: this.isShowable('total_crm', this.deviceType, this.providerFirstIdActivated, this.firstPartyCookie, this.providerUtiqActivated)
            },
            ]
        }
    },
    methods: {
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'getFormulaMetrics', 'retrieveExpiringTransfers']),
        retrieveVolumes(loading, field, device) {
            let count = null
            let adaptedField = field
            if (loading || !this.volumesCount) return count
            if (device === 'all') {
                adaptedField = field.replace(/^total_/, 'total_users_')
                count = this.volumesCount.total_xdevice[adaptedField]
            } else {
                count = this.volumesCount[adaptedField]
            }
            return formatNumber(count)
        },
        isShowable(key, device, firstIdActivated, firstPartyCookie, utiqActivated) {
            if (key === 'total_users_desktop' && device === 'based') return false
            if (key === 'total_firstid' && !firstIdActivated) return false
            if (key === 'total_firstid' && !firstIdActivated) return false
            if (key === 'total_desktop_first' && !firstPartyCookie) return false
            if (
                (
                    key === 'total_utiq_home' ||
                    key === 'total_utiq_mobile'
                )
                && !utiqActivated
            ) return false
            if (['total_desktop_third_customer', 'total_desktop_third_weborama'].includes(key) && device === 'all') return false
            return true
        }
    }
}
</script>

<style lang="scss" scoped>
.volumes_content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(32%, 1fr));
  gap: 20px;
  margin-top: 20px;
}
.volumes_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .volumes_title {
    display: flex;
    align-items: center;
    gap: 10px;
    .svg_icon_position {
      display: inline-flex;
      bottom: 19px;
    }
  .total_volumes {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 16px;
    font-size: 13px;
    bottom: 16px;
    width: fit-content;
    padding: 8px;
    background-color: rgba(243, 232, 255, 1);
    color: rgba(130, 0, 219, 1)
  }
  }
  .select_device {
    display: flex;
    align-items: center;
    width: 18rem;
  }
}
</style>